import type { FormEvent, MouseEvent, Reducer } from 'react'
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import type GlobalSearchState from 'contexts/GlobalSearch/GlobalSearchState'
import type { GlobalSearchAction } from 'contexts/GlobalSearch/GlobalSearchState'
import { GLOBAL_SEARCH_INITIAL_STATE, GlobalSearchStateActions, globalSearchStateReducer } from 'contexts/GlobalSearch/GlobalSearchState'
import SearchLocationInput from 'components/Search/SearchForm/SearchLocationInput/SearchLocationInput'
import SearchDateInputDesktop from 'components/Search/SearchForm/SearchDateInput/SearchDateInputDesktop'
import SearchTravellersInputDesktop from 'components/Search/SearchForm/SearchTravellersInput/SearchTravellersInputDesktop'
import ExperiencesSearchDateSelectDropdown from 'components/Search/Experiences/ExperiencesSearchDateSelectDropdown'
import TextButton from 'LuxKit/Button/TextButton'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import { SearchMenuStates } from 'components/Search/type'
import { DATE_SEARCH_OPTION_IDS, DEFAULT_FLEXIBLE_DURATION_RANGE, HOTEL_SEARCH_TYPEAHEAD_TYPES } from 'constants/search'
import { TRIP_EXPERIENCE_TYPEAHEAD_SEARCH_TYPES, TRIP_PLACE_TYPEAHEAD_TYPES } from 'tripPlanner/config/search'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { getRecentSearches } from 'components/Search/utils'
import useExperienceDateDisplayValue from 'components/Experiences/hooks/useExperienceDateDisplayValue'
import zIndex from 'styles/tools/z-index'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { isValidOccupancy, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { addFlexibleMonthsIfNoneSelected } from 'lib/search/searchUtils'
import DrawerSortControl, { type DrawerSortOption } from './DrawerSortControl'
import ListingDrawerMobileHotelDateFields from './ListingDrawerMobileHotelDateFields'
import ListingDrawerMobileExperienceDateFields from './ListingDrawerMobileExperienceDateFields'
import {
  isListingDrawerHotelDateSelection,
  type ListingDrawerHotelDateSelection,
  type ListingDrawerHotelFlexibleNights,
} from './ListingDrawerSearchDates'

export type DrawerSortValue = Experiences.SortTypes

interface Props {
  searchLabel: string
  searchValue: string
  searchItem?: App.SearchItem
  searchPlaceholder: string
  onSearch: (searchValue: string, searchItem?: App.SearchItem) => void
  searchVertical?: App.SearchPlaceVertical
  checkIn?: string
  checkOut?: string
  onDateChange?: (dates: { checkIn?: string, checkOut?: string }) => void
  hotelDateSelection?: ListingDrawerHotelDateSelection
  onHotelDateSelectionChange?: (selection: ListingDrawerHotelDateSelection) => void
  guests?: number
  rooms?: Array<App.Occupants>
  maxGuests?: number
  onGuestsChange?: (guests: number) => void
  onRoomsChange?: (rooms: Array<App.Occupants>) => void
  showCheckout?: boolean
  showGuests?: boolean
  onSearchItemChange?: (searchItem?: App.SearchItem) => void
  sortValue?: DrawerSortValue
  sortOptions?: Array<DrawerSortOption<DrawerSortValue>>
  onSortChange?: (sort: DrawerSortValue) => void
  showRefineControls?: boolean
  whenLabel?: string
  experienceTripDateRange?: { start: string, end: string }
}

const HOTEL_FLEXIBLE_NIGHTS: ReadonlySet<string> = new Set(
  Object.values(DEFAULT_FLEXIBLE_DURATION_RANGE).filter(
    (value): value is ListingDrawerHotelFlexibleNights =>
      value !== DEFAULT_FLEXIBLE_DURATION_RANGE.EMPTY,
  ),
)

function isHotelFlexibleNights(
  value: string,
): value is ListingDrawerHotelFlexibleNights {
  return HOTEL_FLEXIBLE_NIGHTS.has(value)
}

function getDateSearchOptionId(
  selection: ListingDrawerHotelDateSelection,
): DATE_SEARCH_OPTION_IDS {
  if (selection.type === 'specific') return DATE_SEARCH_OPTION_IDS.SPECIFIC
  if (selection.type === 'flexible') return DATE_SEARCH_OPTION_IDS.FLEXIBLE
  return DATE_SEARCH_OPTION_IDS.ANYTIME
}

const Form = styled.form`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};
  align-items: stretch;
`

const ControlsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  position: relative;
  z-index: ${zIndex.menuInModal};
  width: 100%;
`

const SearchFieldContainer = styled.div`
  flex: 1 1 ${rem(260)};
  width: auto;
`

const SearchField = styled(SearchLocationInput)`
  width: 100%;
`

const DateInputContainer = styled.div`
  flex: 1 1 100%;
  width: 100%;
  min-width: 0;

  ${mediaQueryUp.tablet} {
    flex: 0 1 ${rem(360)};
    min-width: ${rem(300)};
    width: auto;
  }
`

const ExperienceDateInputContainer = styled.div`
  flex: 1 1 100%;
  width: 100%;
  min-width: 0;

  ${mediaQueryUp.tablet} {
    flex: 0 1 ${rem(220)};
    min-width: ${rem(180)};
    width: auto;
  }
`

const TravellersInputContainer = styled.div`
  flex: 0 1 ${rem(220)};
  min-width: ${rem(180)};
`

const StyledSearchFormField = styled(SearchFormField)`
  width: 100%;
`

const SearchButton = styled(TextButton)`
  min-height: ${rem(50)};
`

const RefineRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};
  justify-content: space-between;
  align-items: center;
`

type DisplayOnlySearchItem = App.SearchDestination & { displayOnlySearchItem: true }

function isDisplayOnlySearchItem(searchItem?: App.SearchItem): searchItem is DisplayOnlySearchItem {
  return searchItem?.searchType === 'destination' && 'displayOnlySearchItem' in searchItem
}

function buildDisplayOnlySearchItem(label: string): DisplayOnlySearchItem | undefined {
  const trimmedLabel = label.trim()
  if (!trimmedLabel) return undefined

  return {
    searchType: 'destination',
    value: trimmedLabel,
    displayOnlySearchItem: true,
    format: {
      mainText: trimmedLabel,
    },
  }
}

function getSubmitSearchItem(searchValue: string, searchItem?: App.SearchItem): App.SearchItem | undefined {
  const trimmedSearchValue = searchValue.trim()
  if (!searchItem || searchItem.format.mainText.trim() !== trimmedSearchValue) return undefined

  if (isDisplayOnlySearchItem(searchItem)) return undefined

  return searchItem
}

function createOccupancy(guests?: number): App.Occupants {
  return {
    adults: guests ?? 2,
  }
}

export function getHotelDateSelectionFromSearchState(
  searchState: Pick<GlobalSearchState,
  | 'dateSearchOptionId'
  | 'checkinDate'
  | 'checkoutDate'
  | 'flexibleMonths'
  | 'flexibleNights'
  | 'userSelectedFlexibleMonths'
  >,
): ListingDrawerHotelDateSelection {
  if (searchState.checkinDate && searchState.checkoutDate) {
    return {
      type: 'specific',
      checkIn: searchState.checkinDate.format(ISO_DATE_FORMAT),
      checkOut: searchState.checkoutDate.format(ISO_DATE_FORMAT),
    }
  }

  if (searchState.dateSearchOptionId === DATE_SEARCH_OPTION_IDS.FLEXIBLE) {
    const flexibleNights = isHotelFlexibleNights(searchState.flexibleNights)
      ? searchState.flexibleNights
      : DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION
    return {
      type: 'flexible',
      flexibleMonths: addFlexibleMonthsIfNoneSelected(
        flexibleNights,
        searchState.flexibleMonths,
      ),
      flexibleNights,
      userSelectedFlexibleMonths: searchState.userSelectedFlexibleMonths,
    }
  }

  return { type: 'anytime' }
}

function ListingDrawerSearchControls({
  searchLabel,
  searchValue,
  searchItem,
  searchPlaceholder,
  onSearch,
  searchVertical,
  checkIn,
  checkOut,
  onDateChange,
  hotelDateSelection,
  onHotelDateSelectionChange,
  guests,
  rooms: selectedRooms,
  maxGuests = 12,
  onGuestsChange,
  onRoomsChange,
  showCheckout = false,
  showGuests = false,
  onSearchItemChange,
  sortValue,
  sortOptions,
  onSortChange,
  showRefineControls = false,
  whenLabel,
  experienceTripDateRange,
}: Props) {
  const intl = useIntl()
  const isCompactDatePicker = !useScreenSizeUp('tablet')
  const [draftSearch, setDraftSearch] = useState(searchValue)
  const [draftSearchItem, setDraftSearchItem] = useState<App.SearchItem | undefined>(undefined)
  const [isHotelDatePanelOpen, setIsHotelDatePanelOpen] = useState(false)
  const [isExperienceDatePanelOpen, setIsExperienceDatePanelOpen] = useState(false)
  const defaultRooms = useMemo(
    () => selectedRooms ?? [createOccupancy(guests)],
    [guests, selectedRooms],
  )
  const committedHotelDateSelection = useMemo<ListingDrawerHotelDateSelection>(() => {
    if (hotelDateSelection) return hotelDateSelection
    return checkIn && checkOut
      ? { type: 'specific', checkIn, checkOut }
      : { type: 'anytime' }
  }, [checkIn, checkOut, hotelDateSelection])
  const [searchState, searchDispatch] = useReducer(
    globalSearchStateReducer as Reducer<GlobalSearchState, GlobalSearchAction>,
    {
      ...GLOBAL_SEARCH_INITIAL_STATE,
      searchItem: searchItem ?? buildDisplayOnlySearchItem(searchValue),
      checkinDate: committedHotelDateSelection.type === 'specific' ? moment(committedHotelDateSelection.checkIn) : undefined,
      checkoutDate: committedHotelDateSelection.type === 'specific' ? moment(committedHotelDateSelection.checkOut) : undefined,
      flexibleMonths: committedHotelDateSelection.type === 'flexible' ? committedHotelDateSelection.flexibleMonths : undefined,
      flexibleNights: committedHotelDateSelection.type === 'flexible' ? committedHotelDateSelection.flexibleNights : DEFAULT_FLEXIBLE_DURATION_RANGE.EMPTY,
      userSelectedFlexibleMonths: committedHotelDateSelection.type === 'flexible' && committedHotelDateSelection.userSelectedFlexibleMonths,
      dateSearchOptionId: getDateSearchOptionId(committedHotelDateSelection),
      isAnytimeDateSelected: committedHotelDateSelection.type === 'anytime',
      occupancies: defaultRooms,
    },
  )
  const controlsContainerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const experienceDateTriggerRef = useRef<HTMLButtonElement>(null)

  const restoreCommittedDates = useCallback(() => {
    if (committedHotelDateSelection.type === 'specific') {
      searchDispatch({ type: GlobalSearchStateActions.SET_CHECKIN_DATE, date: moment(committedHotelDateSelection.checkIn) })
      searchDispatch({ type: GlobalSearchStateActions.SET_CHECKOUT_DATE, date: moment(committedHotelDateSelection.checkOut) })
    } else {
      searchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKIN_DATE })
      searchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKOUT_DATE })
    }

    searchDispatch({ type: GlobalSearchStateActions.UNSET_FLEXIBLE_DURATION })
    searchDispatch({ type: GlobalSearchStateActions.UNSET_FLEXIBLE_MONTH_RANGE })
    searchDispatch({ type: GlobalSearchStateActions.UNSET_USER_SELECTED_FLEXIBLE_MONTHS })

    if (committedHotelDateSelection.type === 'flexible') {
      searchDispatch({
        type: GlobalSearchStateActions.SET_FLEXIBLE_DURATION,
        flexibleNights: committedHotelDateSelection.flexibleNights,
      })
      if (committedHotelDateSelection.flexibleMonths) {
        searchDispatch({
          type: GlobalSearchStateActions.SET_FLEXIBLE_MONTH_RANGE,
          flexibleMonths: committedHotelDateSelection.flexibleMonths,
        })
      }
      if (committedHotelDateSelection.userSelectedFlexibleMonths) {
        searchDispatch({
          type: GlobalSearchStateActions.SET_USER_SELECTED_FLEXIBLE_MONTHS,
          userSelectedFlexibleMonths: true,
        })
      }
    }

    searchDispatch({
      type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION,
      optionId: getDateSearchOptionId(committedHotelDateSelection),
    })
    searchDispatch({
      type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED,
      selected: committedHotelDateSelection.type === 'anytime',
    })
  }, [committedHotelDateSelection])

  useEffect(() => {
    setDraftSearch(searchValue)
    setDraftSearchItem(searchItem)
    searchDispatch({
      type: GlobalSearchStateActions.SET_SEARCH_ITEM,
      searchItem: searchItem ?? buildDisplayOnlySearchItem(searchValue),
    })
  }, [searchItem, searchValue])

  useEffect(() => {
    if (isHotelDatePanelOpen || isExperienceDatePanelOpen) return
    restoreCommittedDates()
  }, [
    isExperienceDatePanelOpen,
    isHotelDatePanelOpen,
    restoreCommittedDates,
  ])

  useEffect(() => {
    searchDispatch({ type: GlobalSearchStateActions.SET_OCCUPANCIES, occupancies: defaultRooms })
  }, [defaultRooms])

  const searchButtonLabel = intl.formatMessage({
    id: 'g0txbn0Fo4rljTc7oksG',
    defaultMessage: 'Search',
    description: 'Button label to submit hotel or experience search refinements in the trip planner drawer',
  })

  const anytimeLabel = intl.formatMessage({
    id: 'RigImioe5lWt2RsFEZL9',
    defaultMessage: 'Anytime',
    description: 'Placeholder for date field indicating any date is acceptable',
  })

  const searchTypes = searchVertical === 'experience' ? TRIP_EXPERIENCE_TYPEAHEAD_SEARCH_TYPES : undefined
  const placeTypes = searchVertical === 'experience' ? TRIP_PLACE_TYPEAHEAD_TYPES : HOTEL_SEARCH_TYPEAHEAD_TYPES
  const recentSearchItems = useMemo(
    () => searchVertical === 'hotel' ? getRecentSearches() : undefined,
    [searchVertical],
  )
  const experienceDateFieldValue = useExperienceDateDisplayValue(
    searchState.checkinDate,
    searchState.checkoutDate,
  )
  const experienceTripDateRangeMoments = useMemo(() => {
    if (!experienceTripDateRange) return undefined

    return {
      start: moment(experienceTripDateRange.start),
      end: moment(experienceTripDateRange.end),
    }
  }, [experienceTripDateRange])
  const rooms = useMemo(
    () => searchState.occupancies ?? defaultRooms,
    [defaultRooms, searchState.occupancies],
  )
  const areRoomsValid = rooms.length > 0 && sumUpOccupancies(rooms) <= maxGuests && isValidOccupancy(...rooms)
  const showSortControl = showRefineControls && !!sortValue && !!sortOptions?.length && !!onSortChange

  const closeMenu = useCallback(() => {
    searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: SearchMenuStates.Closed })
  }, [])

  const closeDateMenu = useCallback(() => {
    restoreCommittedDates()
    closeMenu()
  }, [closeMenu, restoreCommittedDates])

  const openMenu = useCallback((menu: SearchMenuStates, event?: MouseEvent<HTMLElement>) => {
    event?.stopPropagation()
    searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu })
  }, [])

  const handleRoomChange = useCallback((occupancies: Array<App.Occupants>) => {
    searchDispatch({ type: GlobalSearchStateActions.SET_OCCUPANCIES, occupancies })
  }, [])

  const commitHotelDateSelection = useCallback((
    hotelDatesOverride?: ListingDrawerHotelDateSelection,
  ) => {
    const resolvedOverride = isListingDrawerHotelDateSelection(hotelDatesOverride)
      ? hotelDatesOverride
      : undefined
    const nextSelection = resolvedOverride ?? getHotelDateSelectionFromSearchState(searchState)

    if (onHotelDateSelectionChange) {
      onHotelDateSelectionChange(nextSelection)
      return
    }

    if (onDateChange) {
      if (nextSelection.type === 'specific') {
        onDateChange({
          checkIn: nextSelection.checkIn,
          checkOut: searchVertical === 'experience' ? nextSelection.checkOut : undefined,
        })
        return
      }

      onDateChange({
        checkIn: undefined,
        checkOut: undefined,
      })
    }
  }, [onDateChange, onHotelDateSelectionChange, searchState, searchVertical])

  const applySearch = useCallback((hotelDatesOverride?: ListingDrawerHotelDateSelection) => {
    if (!areRoomsValid) {
      searchDispatch({ type: GlobalSearchStateActions.TOGGLE_ROOM_ERRORS, shown: true })
      return
    }

    const trimmedSearch = draftSearch.trim()
    onSearch(trimmedSearch, getSubmitSearchItem(trimmedSearch, draftSearchItem))
    commitHotelDateSelection(hotelDatesOverride)

    if (rooms.length > 0) {
      onRoomsChange?.(rooms)
    }

    const selectedGuests = sumUpOccupancies(rooms)
    if (!onRoomsChange && selectedGuests > 0) {
      onGuestsChange?.(Math.min(selectedGuests, maxGuests))
    }
  }, [areRoomsValid, commitHotelDateSelection, draftSearch, draftSearchItem, maxGuests, onGuestsChange, onRoomsChange, onSearch, rooms])

  const unsetDates = useCallback(() => {
    searchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKIN_DATE })
    searchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKOUT_DATE })
  }, [])

  const unsetFlexibleDates = useCallback(() => {
    searchDispatch({ type: GlobalSearchStateActions.UNSET_FLEXIBLE_DURATION })
    searchDispatch({ type: GlobalSearchStateActions.UNSET_FLEXIBLE_MONTH_RANGE })
    searchDispatch({ type: GlobalSearchStateActions.UNSET_USER_SELECTED_FLEXIBLE_MONTHS })
  }, [])

  const selectSpecificDates = useCallback(() => {
    unsetFlexibleDates()
    searchDispatch({ type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED, selected: false })
    searchDispatch({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: DATE_SEARCH_OPTION_IDS.SPECIFIC })
  }, [unsetFlexibleDates])

  const selectFlexibleDates = useCallback(() => {
    unsetDates()
    searchDispatch({ type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED, selected: false })
    searchDispatch({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: DATE_SEARCH_OPTION_IDS.FLEXIBLE })
    if (searchState.flexibleNights === DEFAULT_FLEXIBLE_DURATION_RANGE.EMPTY) {
      searchDispatch({
        type: GlobalSearchStateActions.SET_FLEXIBLE_DURATION,
        flexibleNights: DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION,
      })
    }
  }, [searchState.flexibleNights, unsetDates])

  const selectAnytimeDate = useCallback((shouldApply: boolean) => {
    unsetDates()
    unsetFlexibleDates()
    searchDispatch({ type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED, selected: true })
    searchDispatch({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: DATE_SEARCH_OPTION_IDS.ANYTIME })
    closeMenu()

    if (shouldApply) {
      applySearch({ type: 'anytime' })
    }
  }, [applySearch, closeMenu, unsetDates, unsetFlexibleDates])

  const handleSearchItemChange = useCallback((searchItem?: App.SearchItem) => {
    onSearchItemChange?.(searchItem)
    setDraftSearchItem(searchItem)
    searchDispatch({
      type: GlobalSearchStateActions.SET_SEARCH_ITEM,
      searchItem: searchItem ?? buildDisplayOnlySearchItem(draftSearch),
    })
    if (searchItem) {
      setDraftSearch(searchItem.format.mainText)
    }
  }, [draftSearch, onSearchItemChange])

  const handlePhraseChange = useCallback((value: string) => {
    setDraftSearch(value)
    onSearchItemChange?.(undefined)
    setDraftSearchItem((searchItem) => {
      if (!searchItem || searchItem.format.mainText.trim() === value.trim()) return searchItem
      return undefined
    })
  }, [onSearchItemChange])

  const handleSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    applySearch()
  }, [applySearch])

  const handleDatesApply = useCallback(() => {
    commitHotelDateSelection()
    closeMenu()
  }, [closeMenu, commitHotelDateSelection])

  const handleRoomsApply = useCallback(() => {
    applySearch()
    closeMenu()
  }, [applySearch, closeMenu])

  const handleDatesDecline = useCallback(() => {
    restoreCommittedDates()
    closeMenu()
  }, [closeMenu, restoreCommittedDates])

  const openHotelDatePanel = useCallback(() => {
    setIsHotelDatePanelOpen(true)
  }, [])

  const closeHotelDatePanel = useCallback(() => {
    restoreCommittedDates()
    setIsHotelDatePanelOpen(false)
  }, [restoreCommittedDates])

  const confirmHotelDatePanel = useCallback((selectionOverride?: ListingDrawerHotelDateSelection) => {
    commitHotelDateSelection(selectionOverride)
    setIsHotelDatePanelOpen(false)
  }, [commitHotelDateSelection])

  const openExperienceDatePanel = useCallback(() => {
    setIsExperienceDatePanelOpen(true)
  }, [])

  const closeExperienceDatePanel = useCallback(() => {
    restoreCommittedDates()
    setIsExperienceDatePanelOpen(false)
  }, [restoreCommittedDates])

  const confirmExperienceDatePanel = useCallback(() => {
    commitHotelDateSelection()
    setIsExperienceDatePanelOpen(false)
  }, [commitHotelDateSelection])

  const experienceWhenLabel = whenLabel ?? anytimeLabel

  const renderExperienceDateInput = useCallback(() => (
    <ExperienceDateInputContainer>
      {isCompactDatePicker ? (
        <ListingDrawerMobileExperienceDateFields
          isOpen={isExperienceDatePanelOpen}
          onOpen={openExperienceDatePanel}
          onClose={closeExperienceDatePanel}
          onConfirm={confirmExperienceDatePanel}
          whenLabel={experienceWhenLabel}
          anytimePlaceholder={anytimeLabel}
          tripDateRange={experienceTripDateRangeMoments}
        />
      ) : (
        <>
          <StyledSearchFormField
            ref={experienceDateTriggerRef}
            label={whenLabel}
            placeholder={anytimeLabel}
            value={experienceDateFieldValue}
            onClick={(event) => openMenu(SearchMenuStates.Dates, event)}
          />
          <ExperiencesSearchDateSelectDropdown
            anchorRef={controlsContainerRef}
            triggerRef={experienceDateTriggerRef}
            open={searchState.activeMenu === SearchMenuStates.Dates}
            onApply={handleDatesApply}
            onDecline={handleDatesDecline}
            tripDateRange={experienceTripDateRangeMoments}
            showNextBooking={false}
          />
        </>
      )}
    </ExperienceDateInputContainer>
  ), [
    anytimeLabel,
    closeExperienceDatePanel,
    confirmExperienceDatePanel,
    experienceDateFieldValue,
    experienceTripDateRangeMoments,
    experienceWhenLabel,
    handleDatesApply,
    handleDatesDecline,
    isCompactDatePicker,
    isExperienceDatePanelOpen,
    openExperienceDatePanel,
    openMenu,
    searchState.activeMenu,
    whenLabel,
  ])

  return (
    <ControlsContainer ref={controlsContainerRef}>
      <GlobalSearchContextProvider state={searchState} dispatch={searchDispatch}>
        <Form onSubmit={handleSubmit}>
          <SearchFieldContainer>
            <SearchField
              label={searchLabel}
              placeholder={searchPlaceholder}
              onChange={handleSearchItemChange}
              onPhraseChange={handlePhraseChange}
              variant="dropdown"
              dropdownSize="fill-anchor"
              inputRef={searchInputRef}
              controlsContainerRef={controlsContainerRef}
              searchVertical={searchVertical}
              searchTypes={searchTypes}
              placeTypes={placeTypes}
              recentSearchItems={recentSearchItems}
              showNaturalLanguageSearchSuggestions={searchVertical === 'hotel'}
            />
          </SearchFieldContainer>

          {onHotelDateSelectionChange && showCheckout && (
            <DateInputContainer>
              {isCompactDatePicker ? (
                <ListingDrawerMobileHotelDateFields
                  isOpen={isHotelDatePanelOpen}
                  onOpen={openHotelDatePanel}
                  onClose={closeHotelDatePanel}
                  onConfirm={confirmHotelDatePanel}
                  anytimePlaceholder={anytimeLabel}
                />
              ) : (
                <SearchDateInputDesktop
                  dropdownAnchorRef={controlsContainerRef}
                  onDateToggle={(event) => openMenu(SearchMenuStates.Dates, event)}
                  isToggled={searchState.activeMenu === SearchMenuStates.Dates}
                  onDatesApply={handleDatesApply}
                  onDatesDecline={handleDatesDecline}
                  anytimeDateSelected={searchState.isAnytimeDateSelected}
                  selectAnytimeDate={selectAnytimeDate}
                  selectSpecificDates={selectSpecificDates}
                  selectFlexibleDates={selectFlexibleDates}
                  dateSearchOptionId={searchState.dateSearchOptionId}
                  closeMenu={closeDateMenu}
                />
              )}
            </DateInputContainer>
          )}

          {onDateChange && !showCheckout && renderExperienceDateInput()}

          {showGuests && (
            <TravellersInputContainer>
              <SearchTravellersInputDesktop
                dropdownAnchorRef={controlsContainerRef}
                rooms={rooms}
                onTravellersToggle={(event) => openMenu(SearchMenuStates.Rooms, event)}
                isToggled={searchState.activeMenu === SearchMenuStates.Rooms}
                onRoomChange={handleRoomChange}
                onRoomsApply={handleRoomsApply}
                closeMenu={closeMenu}
                error={searchState.areRoomErrorsShown && !areRoomsValid}
              />
            </TravellersInputContainer>
          )}

          <SearchButton kind="primary" size="large" type="submit">
            {searchButtonLabel}
          </SearchButton>
        </Form>
      </GlobalSearchContextProvider>

      {showSortControl && sortValue && sortOptions && onSortChange && (
        <RefineRow>
          <DrawerSortControl sortValue={sortValue} sortOptions={sortOptions} onSortChange={onSortChange} />
        </RefineRow>
      )}
    </ControlsContainer>
  )
}

export default ListingDrawerSearchControls
